import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "Which statement about saving is most accurate?",
    options: [
      "'Saving is only for emergencies.'",
      "'Saving helps you prepare for the future.'",
      "'Saving is not important.'"
    ],
    correctAnswer: 1,
    explanation: "Saving is important for everyone. It helps you prepare for the future, handle emergencies, and achieve your financial goals."
  },
  {
    question: "What does the IFDFW assessment primarily measure?",
    options: [
      "Your overall financial well-being and satisfaction",
      "Your credit score",
      "Your investment portfolio performance"
    ],
    correctAnswer: 0,
    explanation: "The IFDFW (I Feel Damned Wealthy) assessment measures your overall financial well-being, including your satisfaction with your financial situation, security, and freedom."
  },
  {
    question: "How should you interpret IFDFW assessment results?",
    options: [
      "Use them as a starting point for improvement, not as final judgments",
      "Consider them permanent indicators of your financial status",
      "Ignore scores below a certain threshold"
    ],
    correctAnswer: 0,
    explanation: "Assessment results should be used as a starting point for understanding your current financial well-being and identifying areas for growth, rather than as permanent judgments."
  },
  {
    question: "Which factor is most important for long-term financial satisfaction?",
    options: [
      "Aligning spending with personal values and goals",
      "Having the highest possible income",
      "Owning the most expensive possessions"
    ],
    correctAnswer: 0,
    explanation: "Long-term financial satisfaction comes from aligning your spending and saving habits with your personal values and life goals, rather than focusing solely on income or possessions."
  },
  {
    question: "What role does financial literacy play in well-being?",
    options: [
      "It empowers informed decision-making and reduces anxiety",
      "It guarantees financial success",
      "It replaces the need for professional advice"
    ],
    correctAnswer: 0,
    explanation: "Financial literacy empowers you to make informed decisions, understand financial products, and reduce anxiety by giving you control over your financial future."
  }
];

const FinancialModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('financialM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Measuring financial well-being
              </li>
              <li>
                IFDFW assessment interpretation
              </li>
              <li>
                Personal financial growth strategies
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your financial wellness journey by learning about financial resilience!</p>
            <p>Ready to explore strategies for building long-term financial stability?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/financial-resources')}
          >
            Return to Financial Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Measuring Financial Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about the IFDFW assessment and how to measure financial well-being.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/financial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule3QuizPage;

